import {Box} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import ChartCard from './components/ChartCard';
import ComparisonColumns from './components/ComparisonColumns';
import DashboardState, {CARD_GRID} from './components/DashboardState';
import ProfileRadar from './components/ProfileRadar';
import SectionHeading from './components/SectionHeading';
import {fetchAttentamenteDashboard} from './utils/api';
import {CHARTS, GROWTH_CATEGORIES, IMPROVEMENT_CATEGORIES} from './utils/labels';
import {asSingleCategory, sizes, SINGLE_CATEGORY} from './utils/single';
import {BENEFICIARIES_MAPPING} from './utils/surveys';
import {BeneficiariesData} from './utils/types';
import {useAttentamente2024Auth} from './utils/useAttentamente2024Auth';

const trendSizes = (trend: BeneficiariesData['distress_trend' | 'activities_trend' | 'network_trend']) => ({
    project: trend.project.stable.n,
    bando: trend.bando.stable.n,
});

const Attentamente2024Beneficiaries = () => {
    const {container, uoaId, surveyIds, isAuthorized} = useAttentamente2024Auth(['base_id', 'update_id', 'final_id']);

    const {data, isLoading, isError} = useQuery<BeneficiariesData>({
        queryKey: ['attentamente-2024-beneficiaries', container, uoaId, surveyIds],
        queryFn: () => fetchAttentamenteDashboard(container!, uoaId!, 'beneficiaries', {...surveyIds, ...BENEFICIARIES_MAPPING}),
        enabled: isAuthorized,
        staleTime: 5 * 60 * 1000,
    });

    return (
        <DashboardState isAuthorized={isAuthorized} isLoading={isLoading} isError={isError || (!isLoading && !data)} cards={8}>
            {() => (
                <>
                    <SectionHeading
                        title='Situazione iniziale'
                        description="Come si presentavano i beneficiari all'avvio del percorso, secondo la scheda iniziale."
                    >
                        <Box sx={CARD_GRID}>
                            <ChartCard {...CHARTS.distressAverage} n={sizes(data!.distress_average)}>
                                <ComparisonColumns categories={SINGLE_CATEGORY} data={asSingleCategory(data!.distress_average)} />
                            </ChartCard>
                            <ChartCard {...CHARTS.inServices} n={sizes(data!.in_services)}>
                                <ComparisonColumns categories={SINGLE_CATEGORY} data={asSingleCategory(data!.in_services)} suffix='%' max={100} />
                            </ChartCard>
                            <ChartCard {...CHARTS.fragilityIndex} n={sizes(data!.fragility_index)}>
                                <ComparisonColumns categories={SINGLE_CATEGORY} data={asSingleCategory(data!.fragility_index)} max={100} />
                            </ChartCard>
                            <ChartCard {...CHARTS.profile} note='La numerosità cambia per ciascuna condizione: passa sul grafico per vederla.'>
                                <ProfileRadar data={data!.profile} />
                            </ChartCard>
                        </Box>
                    </SectionHeading>
                    <SectionHeading
                        title='Evoluzione della situazione'
                        description="Confronto tra la scheda iniziale e la scheda finale di ciascun beneficiario o, se manca, l'aggiornamento più recente."
                    >
                        <Box sx={CARD_GRID}>
                            <ChartCard {...CHARTS.newServices} n={sizes(data!.new_services)}>
                                <ComparisonColumns categories={SINGLE_CATEGORY} data={asSingleCategory(data!.new_services)} suffix='%' max={100} />
                            </ChartCard>
                            <ChartCard {...CHARTS.distressTrend} n={trendSizes(data!.distress_trend)}>
                                <ComparisonColumns categories={IMPROVEMENT_CATEGORIES} data={data!.distress_trend} suffix='%' max={100} />
                            </ChartCard>
                            <ChartCard {...CHARTS.activitiesTrend} n={trendSizes(data!.activities_trend)}>
                                <ComparisonColumns categories={GROWTH_CATEGORIES} data={data!.activities_trend} suffix='%' max={100} />
                            </ChartCard>
                            <ChartCard {...CHARTS.networkTrend} n={trendSizes(data!.network_trend)}>
                                <ComparisonColumns categories={GROWTH_CATEGORIES} data={data!.network_trend} suffix='%' max={100} />
                            </ChartCard>
                        </Box>
                    </SectionHeading>
                </>
            )}
        </DashboardState>
    );
};

export default Attentamente2024Beneficiaries;
